<script setup lang="ts">
import { mdiClock } from '@mdi/js';
import { computed, ref, useAttrs, watch } from 'vue';
import { getPrefixedUuid } from '../utils';
import { DatePickerProps } from '../types';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import FormInputDisplay from '../display/FormInputDisplay.vue';
import TextField from '../TextField/TextField.vue';
import { DateTime } from 'luxon';
import { useToggle } from '@vueuse/core';
import { maskitoTimeOptionsGenerator } from '@maskito/kit';

const maskOptions = maskitoTimeOptionsGenerator({
  mode: 'HH:MM',
});

const timeFormat = 'HH:mm';

const props = withDefaults(defineProps<DatePickerProps>(), {
  mode: 'edit',
});

const timePickerModelValue = ref<string | undefined>();
const inputModelValue = ref<string | undefined>();

const timePickerFormat = {
  suppressSeconds: true,
  suppressMilliseconds: true,
  includeOffset: false,
  extendedZone: false,
};

watch(
  () => props.modelValue,
  (modelValue) => {
    const time = modelValue?.isValid
      ? modelValue.set({ second: 0, millisecond: 0 }).toISOTime(timePickerFormat) || undefined
      : undefined;

    timePickerModelValue.value = time;
    inputModelValue.value = time;
  },
  {
    immediate: true,
  },
);

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  placeholder: timeFormat,
}));

const attrs = useAttrs();

const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const id = getPrefixedUuid('datePicker');

const emits = defineEmits(['update:modelValue']);

const [pickerOpen, togglePicker] = useToggle(false);

const zeroSeconds = { second: 0, millisecond: 0 };

function updatePicker(hour?: number, minute?: number) {
  const newTime = props.modelValue
    ? props.modelValue?.set({ hour, minute, ...zeroSeconds })
    : DateTime.fromObject({
        hour,
        minute,
        ...zeroSeconds,
      });

  onUpdate(newTime);
}

function inputUpdate(input: string) {
  const newTime = input ? DateTime.fromFormat(input, timeFormat) : undefined;
  if (newTime?.isValid) {
    onUpdate(newTime);
    return;
  }

  // ponytail: mask skeleton with no digits counts as cleared; otherwise keep last valid
  if (!input || !/\d/.test(input)) {
    onUpdate(undefined);
  }
}

function onUpdate(time: DateTime | undefined) {
  if (time?.isValid) {
    const { hour, minute } = time;

    const newTime = props.modelValue
      ? props.modelValue?.set({ hour, minute, ...zeroSeconds })
      : DateTime.fromObject({
          hour,
          minute,
          ...zeroSeconds,
        });

    emits('update:modelValue', newTime.isValid ? newTime : undefined);
  } else {
    emits('update:modelValue', undefined);
  }
}
</script>

<template>
  <div :class="$attrs.class">
    <FormLabel :for="id" :infoMessage="infoMessage" :label="label" :disabled="props.disabled">
      <template #default="{ hoverProps }">
        <FormInputDisplay v-if="mode === 'details'" :value="inputModelValue" />
        <TextField
          v-else
          v-bind="{ ...passdownProps, ...passdownAttrs, ...hoverProps, id }"
          v-model="inputModelValue"
          v-maskito="maskOptions"
          :data-testId="$attrs['data-testid'] ? $attrs['data-testid'] : `${label}_Time`"
          @update:modelValue="inputUpdate"
        >
          <template #prepend-inner>
            <v-menu
              v-model="pickerOpen"
              :closeOnContentClick="false"
              transition="scale-transition"
              location="left"
            >
              <template #activator="{ props: activatorProps }">
                <v-btn
                  variant="text"
                  density="compact"
                  color="secondary"
                  v-bind="activatorProps"
                  :icon="mdiClock"
                  @click="togglePicker"
                />
              </template>

              <v-time-picker
                :modelValue="timePickerModelValue"
                title=""
                format="24hr"
                color="secondary"
                fullWidth
                @update:hour="updatePicker($event)"
                @update:minute="updatePicker(undefined, $event)"
              />
            </v-menu>
          </template>
        </TextField>
      </template>
    </FormLabel>
  </div>
</template>
